/**
 * Pure coordinate/domain math for the simulation timeline editor.
 * Everything here is side-effect free and unit-tested in simulation-math.test.ts.
 */

export interface TimelinePoint {
  /** integer minutes (snapped); driver: from sim start; recovery: RELATIVE to driver end */
  t: number;
  /** percent vs current mid price, snapped to 0.5 steps; may be negative */
  changePct: number;
}

export type Series = 'driver' | 'recovery';

// fixed viewBox geometry (viewBox units)
export const SVG_W = 800;
export const SVG_H = 380;
export const PAD = { top: 24, bottom: 36, left: 56 } as const;
export const CHART_H = SVG_H - PAD.top - PAD.bottom; // 320

const PAD_RIGHT_WITH_PRICE = 80;
const PAD_RIGHT_WITHOUT_PRICE = 24;

// autoscale floors and headroom
const X_FLOOR_MINUTES = 30;
const Y_FLOOR_PCT = 10;
const X_ROUND_STEP = 5;
const Y_ROUND_STEP = 5;
const X_HEADROOM = 5;
const Y_HEADROOM = 5;
const X_STEP_BREAKPOINT = 60;
const Y_STEP_BREAKPOINT = 30;

// price display thresholds
const PRICE_NO_DECIMALS_MIN = 10_000;
const PRICE_TWO_DECIMALS_MIN = 1;
const PRICE_SMALL_DECIMALS = 6;

/** Right padding widens to fit the price-label column when a mid price is known. */
export const getPadRight = (hasPrice: boolean): number => (hasPrice ? PAD_RIGHT_WITH_PRICE : PAD_RIGHT_WITHOUT_PRICE);

/** Plot width in viewBox units — 664 with price, 720 without. */
export const getChartWidth = (hasPrice: boolean): number => SVG_W - PAD.left - getPadRight(hasPrice);

export interface ClientRectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** screen pixels → viewBox units. */
export const clientPointToSvg = (clientX: number, clientY: number, rect: ClientRectLike): { x: number; y: number } => ({
  x: ((clientX - rect.left) / rect.width) * SVG_W,
  y: ((clientY - rect.top) / rect.height) * SVG_H,
});

/** Plot-area hit test — gates hover + click-to-add, NOT drag frames. */
export const isInChart = (x: number, y: number, chartW: number): boolean => x >= PAD.left && x <= PAD.left + chartW && y >= PAD.top && y <= PAD.top + CHART_H;

/** X domain from VISUAL times (recovery t + driverEndTime). Empty → 35; always a multiple of 5. */
export const computeXMax = (visualTimes: readonly number[]): number => Math.ceil(Math.max(X_FLOOR_MINUTES, ...visualTimes) / X_ROUND_STEP) * X_ROUND_STEP + X_HEADROOM;

/** symmetric ±yExtent percent domain. Empty → 15. */
export const computeYExtent = (changePcts: readonly number[]): number => Math.ceil(Math.max(Y_FLOOR_PCT, ...changePcts.map(Math.abs)) / Y_ROUND_STEP) * Y_ROUND_STEP + Y_HEADROOM;

export const getXStep = (xMax: number): number => (xMax <= X_STEP_BREAKPOINT ? 5 : 10);
export const getYStep = (yExtent: number): number => (yExtent <= Y_STEP_BREAKPOINT ? 5 : 10);

/** Inclusive arithmetic range: min, min+step, … ≤ max. */
export const gridRange = (min: number, max: number, step: number): number[] => {
  const values: number[] = [];
  for (let value = min; value <= max; value += step) values.push(value);
  return values;
};

export interface ChartScale {
  padRight: number;
  chartW: number;
  xMax: number;
  yExtent: number;
  xStep: number;
  yStep: number;
  xGridValues: number[];
  yGridValues: number[];
}

/** Bundles the reactive domain computation (recomputed live, including mid-drag values). */
export const computeScale = (visualTimes: readonly number[], changePcts: readonly number[], hasPrice: boolean): ChartScale => {
  const xMax = computeXMax(visualTimes);
  const yExtent = computeYExtent(changePcts);
  const xStep = getXStep(xMax);
  const yStep = getYStep(yExtent);
  return {
    padRight: getPadRight(hasPrice),
    chartW: getChartWidth(hasPrice),
    xMax,
    yExtent,
    xStep,
    yStep,
    xGridValues: gridRange(0, xMax, xStep),
    yGridValues: gridRange(-yExtent, yExtent, yStep),
  };
};

// scales (data ↔ viewBox)
export const toX = (t: number, xMax: number, chartW: number): number => PAD.left + (t / xMax) * chartW;

/** +yExtent → top edge (24); 0 → centre (184); -yExtent → bottom (344). */
export const toY = (pct: number, yExtent: number): number => PAD.top + ((yExtent - pct) / (yExtent * 2)) * CHART_H;

export const svgToData = (x: number, y: number, xMax: number, yExtent: number, chartW: number): { t: number; pct: number } => ({
  t: ((x - PAD.left) / chartW) * xMax,
  pct: yExtent - ((y - PAD.top) / CHART_H) * (yExtent * 2),
});

// snapping and formatting
export const snapTime = (t: number): number => Math.max(0, Math.round(t));
export const snapPct = (pct: number): number => Math.round(pct * 2) / 2;

export const formatPct = (pct: number): string => `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;

export const priceAtPct = (pct: number, currentPrice: number | null | undefined): number => (currentPrice ?? 0) * (1 + pct / 100);

export const formatPrice = (price: number): string => {
  if (price >= PRICE_NO_DECIMALS_MIN) return price.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (price >= PRICE_TWO_DECIMALS_MIN) return price.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return price.toLocaleString(undefined, { maximumFractionDigits: PRICE_SMALL_DECIMALS });
};

/**
 * Serialisation invariants: stable sort by t ascending (ties keep insertion
 * order) and emitted objects are exactly `{ t, changePct }` — internal keys stripped.
 */
export const sortTimeline = (points: readonly TimelinePoint[]): TimelinePoint[] => [...points].sort((a, b) => a.t - b.t).map(({ t, changePct }) => ({ t, changePct }));

export interface DriverEnd {
  driverEndTime: number;
  lastDriverPoint: TimelinePoint;
}

/** The LAST driver point anchors the recovery series and the clamp envelope. */
export const driverEnd = (driver: readonly TimelinePoint[]): DriverEnd => {
  const lastDriverPoint = sortTimeline(driver).at(-1) ?? { t: 0, changePct: 0 };
  return { driverEndTime: lastDriverPoint.t, lastDriverPoint };
};

/**
 * Recovery may only move price from the driver's final level back toward 0.
 * Applied on recovery add and every recovery drag frame ONLY (never re-applied
 * to existing points when the driver later changes).
 */
export const clampRecoveryPct = (pct: number, driverEndPct: number): number => {
  if (driverEndPct < 0) return Math.max(driverEndPct, Math.min(0, pct));
  if (driverEndPct > 0) return Math.min(driverEndPct, Math.max(0, pct));
  return 0;
};

/** Pointer works in visual time; storage is relative to the driver end (floor 0). */
export const toRecoveryTime = (visualT: number, driverEndTime: number): number => Math.max(0, snapTime(visualT) - driverEndTime);

export const polylinePoints = (coords: ReadonlyArray<{ x: number; y: number }>): string => coords.map((c) => `${c.x},${c.y}`).join(' ');
